import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, Loader2, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PROPERTY_TYPES } from "../data/content";

const inputCls = "w-full rounded-xl border border-white/10 bg-navy px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";
const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const emptyForm = {
  transaction: "vente", title: "", city: "Amiens", price: "", surface: "", rooms: "",
  bedrooms: "", property_type: "Appartement", dpe: "", description: "",
  images: "", features: "", lat: "", lng: "",
  speed_detresse: false, carnet_since: "", interventions: "", published: true,
};

function toForm(l) {
  return {
    ...l,
    price: l.price ?? "", surface: l.surface ?? "", rooms: l.rooms ?? "", bedrooms: l.bedrooms ?? "",
    lat: l.lat ?? "", lng: l.lng ?? "",
    speed_detresse: !!l.speed_detresse, carnet_since: l.carnet_since || "",
    images: (l.images || []).join("\n"),
    features: (l.features || []).join(", "),
    interventions: (l.interventions || []).map((i) => `${i.date} | ${i.type} | ${i.description}`).join("\n"),
  };
}

function toPayload(f) {
  return {
    transaction: f.transaction, title: f.title, city: f.city,
    price: Number(f.price) || 0, surface: Number(f.surface) || 0,
    rooms: Number(f.rooms) || 0, bedrooms: Number(f.bedrooms) || 0,
    property_type: f.property_type, dpe: f.dpe, description: f.description,
    lat: Number(f.lat) || 0, lng: Number(f.lng) || 0,
    speed_detresse: !!f.speed_detresse, carnet_since: f.carnet_since || "",
    interventions: (f.interventions || "").split("\n").map((line) => {
      const [date, type, ...rest] = line.split("|").map((x) => x.trim());
      if (!date && !type) return null;
      return { date: date || "", type: type || "", description: rest.join(" | ") || "" };
    }).filter(Boolean),
    images: f.images.split("\n").map((x) => x.trim()).filter(Boolean),
    features: f.features.split(",").map((x) => x.trim()).filter(Boolean),
    published: !!f.published,
  };
}

export default function ListingsManager({ passcode }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | listing object
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.adminListings(passcode).then((d) => setRows(d.listings)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  }, [passcode]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (l) => { setForm(toForm(l)); setEditing(l); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === "new") { await api.adminCreateListing(passcode, payload); toast.success("Annonce créée"); }
      else { await api.adminUpdateListing(passcode, editing.id, payload); toast.success("Annonce mise à jour"); }
      setEditing(null); load();
    } catch { toast.error("Erreur d'enregistrement"); } finally { setSaving(false); }
  };

  const togglePublish = async (l) => {
    try {
      await api.adminUpdateListing(passcode, l.id, { ...toPayload(toForm(l)), published: !l.published });
      toast.success(l.published ? "Annonce dépubliée" : "Annonce publiée");
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Supprimer l'annonce "${l.title}" ?`)) return;
    try { await api.adminDeleteListing(passcode, l.id); toast.success("Annonce supprimée"); load(); }
    catch { toast.error("Erreur de suppression"); }
  };

  return (
    <div data-testid="listings-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{rows.length} annonce(s)</p>
        <button onClick={openNew} data-testid="listing-new-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-gold-light">
          <Plus className="h-4 w-4" /> Nouvelle annonce
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading && <p className="text-slate-500">Chargement…</p>}
        {rows.map((l) => (
          <div key={l.id} data-testid={`listing-admin-${l.reference}`} className="overflow-hidden rounded-2xl border border-white/10 bg-navy-surface/60">
            <div className="relative aspect-[16/10]">
              {l.images?.[0] ? <img src={l.images[0]} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-600"><HomeIcon className="h-8 w-8" /></div>}
              <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium ${l.published ? "bg-emerald-500/80 text-white" : "bg-slate-600/80 text-white"}`}>
                {l.published ? "Publiée" : "Brouillon"}
              </span>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-500">{l.reference} · {l.transaction === "vente" ? "Vente" : "Location"}</p>
              <h4 className="mt-1 font-serif text-lg text-white">{l.title}</h4>
              <p className="text-sm text-gold">{fmt(l.price)}{l.transaction === "location" ? " /mois" : ""}</p>
              <p className="text-xs text-slate-400">{l.city} · {l.surface} m² · {l.rooms} p.</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => openEdit(l)} data-testid={`listing-edit-${l.reference}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/15 py-2 text-xs text-slate-200 hover:border-gold/50"><Pencil className="h-3.5 w-3.5" /> Modifier</button>
                <button onClick={() => togglePublish(l)} data-testid={`listing-toggle-${l.reference}`} className="flex items-center justify-center rounded-lg border border-white/15 px-3 py-2 text-slate-200 hover:border-gold/50">{l.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button onClick={() => remove(l)} data-testid={`listing-delete-${l.reference}`} className="flex items-center justify-center rounded-lg border border-white/15 px-3 py-2 text-red-300 hover:border-red-400/50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] flex justify-end bg-black/50" onClick={() => setEditing(null)} data-testid="listing-form-drawer">
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-lg overflow-y-auto border-l border-white/10 bg-navy-surface p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-white">{editing === "new" ? "Nouvelle annonce" : "Modifier l'annonce"}</h3>
              <button type="button" onClick={() => setEditing(null)}><X className="h-5 w-5 text-slate-400 hover:text-white" /></button>
            </div>
            <div className="mt-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="lf-transaction" value={form.transaction} onChange={(e) => setForm({ ...form, transaction: e.target.value })} className={inputCls}>
                  <option value="vente" className="bg-navy">Vente</option>
                  <option value="location" className="bg-navy">Location</option>
                </select>
                <select data-testid="lf-type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inputCls}>
                  {PROPERTY_TYPES.map((t) => <option key={t} value={t} className="bg-navy">{t}</option>)}
                </select>
              </div>
              <input required data-testid="lf-title" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              <input required data-testid="lf-city" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" data-testid="lf-price" placeholder="Prix (€)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                <input type="number" data-testid="lf-surface" placeholder="Surface (m²)" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} className={inputCls} />
                <input type="number" data-testid="lf-rooms" placeholder="Pièces" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} className={inputCls} />
                <input type="number" data-testid="lf-bedrooms" placeholder="Chambres" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className={inputCls} />
              </div>
              <input data-testid="lf-dpe" placeholder="DPE (ex : C)" value={form.dpe} onChange={(e) => setForm({ ...form, dpe: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="lf-lat" placeholder="Latitude (ex : 49.8941)" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className={inputCls} />
                <input data-testid="lf-lng" placeholder="Longitude (ex : 2.2958)" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className={inputCls} />
              </div>
              <textarea rows="4" data-testid="lf-description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              <div>
                <label className="mb-1 block text-xs text-slate-400">Images (une URL par ligne)</label>
                <textarea rows="3" data-testid="lf-images" placeholder="https://…" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className={inputCls} />
              </div>
              <input data-testid="lf-features" placeholder="Points forts (séparés par des virgules)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className={inputCls} />

              {/* Carnet de Santé */}
              <div className="rounded-xl border border-gold/25 bg-gold/5 p-4">
                <label className="flex items-center gap-3 text-sm text-white">
                  <input type="checkbox" data-testid="lf-carnet" checked={form.speed_detresse} onChange={(e) => setForm({ ...form, speed_detresse: e.target.checked })} className="accent-gold" />
                  Bien abonné Speed Détresse (Carnet de Santé)
                </label>
                {form.speed_detresse && (
                  <div className="mt-3 space-y-3">
                    <input data-testid="lf-carnet-since" placeholder="Suivi depuis (année, ex : 2021)" value={form.carnet_since} onChange={(e) => setForm({ ...form, carnet_since: e.target.value })} className={inputCls} />
                    <div>
                      <label className="mb-1 block text-xs text-slate-400">Interventions — une par ligne : <span className="text-slate-500">date | type | description</span></label>
                      <textarea rows="4" data-testid="lf-interventions" placeholder="2024-11-05 | Plomberie | Remplacement du mitigeur" value={form.interventions} onChange={(e) => setForm({ ...form, interventions: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-3 text-sm text-slate-300">
                <input type="checkbox" data-testid="lf-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-gold" />
                Publier l'annonce immédiatement
              </label>
            </div>
            <button type="submit" disabled={saving} data-testid="lf-save" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-navy hover:bg-gold-light disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer l'annonce"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
